/**
 * Object storage uploads, console-wide (a module singleton, so leaving the bucket page does not stop them;
 * the floating UploadPanel shows progress anywhere in the console).
 * - Presigned URLs only sign host: no custom headers on the PUTs.
 * - Big files go multipart (parts uploaded 4 at a time). A failed part is retried with backoff; one bad part no
 *   longer kills the upload.
 * - Resume: the multipart upload id is remembered per (bucket, key, size, mtime). Adding the same file again
 *   (after a reload, a crash or a cancel-free close) continues from the parts already stored.
 */
import { s3 } from './common';

const FILE_CONCURRENCY = 2;
const PART_CONCURRENCY = 4;
const PART_RETRIES = 5;
const RESUME_KEY = 'yc_s3_resume';

class UploadCanceled extends Error {
    constructor() {
        super('canceled');
        this.canceled = true;
    }
}

// ---------------------------------------------------------------- resume store (best effort)
const resumeId = (bucketId, key, file) => `${bucketId}|${key}|${file.size}|${file.lastModified || 0}`;
const readResume = () => {
    try { return JSON.parse(localStorage.getItem(RESUME_KEY) || '{}') || {}; } catch (_) { return {}; }
};
const writeResume = (fn) => {
    try {
        const m = readResume();
        fn(m);
        // keep it small: forget entries older than 7 days (the server-side upload is cleaned up anyway)
        const cutoff = Date.now() - 7 * 86400e3;
        Object.keys(m).forEach((k) => { if (!m[k] || m[k].at < cutoff) delete m[k]; });
        localStorage.setItem(RESUME_KEY, JSON.stringify(m));
    } catch (_) { /* storage blocked: no resume */ }
};

// ---------------------------------------------------------------- transport
const putXhr = (url, body, onProgress, registerAbort) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded); };
    xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader('ETag') || '');
        else {
            const err = new Error(`HTTP ${xhr.status}`);
            err.status = xhr.status;
            reject(err);
        }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.onabort = () => reject(new UploadCanceled());
    registerAbort?.(() => xhr.abort());
    xhr.send(body);
});

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ---------------------------------------------------------------- store
let jobs = [];
const listeners = new Set();
let seq = 0;

const emit = () => {
    const snap = jobs.slice();
    listeners.forEach((fn) => { try { fn(snap); } catch (_) { /* ignore */ } });
};
const patch = (uid, p) => {
    jobs = jobs.map((j) => (j.uid === uid ? { ...j, ...p } : j));
    emit();
};
const isActive = (j) => j.status === 'queued' || j.status === 'uploading';

export const subscribe = (fn) => {
    listeners.add(fn);
    fn(jobs.slice());
    return () => listeners.delete(fn);
};
export const getJobs = () => jobs.slice();

// closing / reloading the tab would stop transfers
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', (e) => {
        if (jobs.some(isActive)) {
            e.preventDefault();
            e.returnValue = '';
        }
    });
}

const handles = {}; // uid → {cancel}

const runJob = async (job, ctl) => {
    const { bucketId, file, key } = job;
    const rid = resumeId(bucketId, key, file);
    const saved = readResume()[rid];
    const init = await s3.post(`/buckets/${bucketId}/objects/upload`, {
        key, size: file.size, content_type: file.type || 'application/octet-stream', upload_id: saved?.upload_id || undefined,
    });
    if (ctl.canceled) throw new UploadCanceled();

    if (init?.mode === 'single') {
        await putXhr(init.url, file, (n) => patch(job.uid, { loaded: n }), ctl.register);
        return;
    }
    if (init?.mode !== 'multipart' || !Array.isArray(init.urls)) throw new Error('Unexpected upload response');
    ctl.multipart = { key: init.key || key, upload_id: init.upload_id };
    writeResume((m) => { m[rid] = { upload_id: init.upload_id, at: Date.now() }; });

    const partSize = Number(init.part_size) || 0;
    const urls = init.urls;
    const etags = new Array(urls.length);
    const loaded = new Array(urls.length).fill(0);
    (init.done || []).forEach((d) => {
        const i = Number(d.part) - 1;
        if (i >= 0 && i < urls.length) {
            etags[i] = d.etag;
            loaded[i] = Math.min(partSize, file.size - i * partSize);
        }
    });
    const todo = urls.map((_, i) => i).filter((i) => !etags[i]);
    const report = () => patch(job.uid, {
        loaded: loaded.reduce((a, b) => a + b, 0),
        parts: urls.length,
        partsDone: etags.filter(Boolean).length,
        resumed: (init.done || []).length > 0,
    });
    report();

    let next = 0;
    const worker = async () => {
        while (!ctl.canceled && next < todo.length) {
            const i = todo[next++];
            const blob = file.slice(i * partSize, (i + 1) * partSize);
            for (let attempt = 0; ; attempt++) {
                try {
                    // eslint-disable-next-line no-loop-func
                    const etag = await putXhr(urls[i], blob, (n) => { loaded[i] = n; report(); }, ctl.register);
                    if (!etag) throw new Error('Missing ETag');
                    etags[i] = etag;
                    loaded[i] = blob.size;
                    report();
                    break;
                } catch (e) {
                    if (e?.canceled || ctl.canceled) throw new UploadCanceled();
                    // 403 = URL expired / signature: retrying the same URL won't help
                    if (attempt >= PART_RETRIES || e?.status === 403) throw e;
                    loaded[i] = 0;
                    patch(job.uid, { retrying: true });
                    await sleep(Math.min(15000, 1000 * 2 ** attempt));
                    patch(job.uid, { retrying: false });
                }
            }
        }
        if (ctl.canceled) throw new UploadCanceled();
    };
    await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, todo.length || 1) }, worker));
    if (ctl.canceled) throw new UploadCanceled();

    await s3.post(`/buckets/${bucketId}/objects/complete`, {
        key: ctl.multipart.key,
        upload_id: ctl.multipart.upload_id,
        parts: etags.map((etag, i) => ({ part: i + 1, etag })),
    });
    writeResume((m) => { delete m[rid]; });
    ctl.multipart = null;
};

const pump = () => {
    while (jobs.filter((j) => j.status === 'uploading').length < FILE_CONCURRENCY) {
        const job = jobs.find((j) => j.status === 'queued');
        if (!job) return;
        const aborts = new Set();
        const ctl = {
            canceled: false,
            multipart: null,
            register: (fn) => { aborts.add(fn); if (ctl.canceled) fn(); },
        };
        handles[job.uid] = {
            cancel: () => {
                ctl.canceled = true;
                aborts.forEach((fn) => { try { fn(); } catch (_) { /* ignore */ } });
            },
        };
        patch(job.uid, { status: 'uploading', startedAt: Date.now() });
        runJob(job, ctl)
            .then(() => patch(job.uid, { status: 'done', loaded: job.size, finishedAt: Date.now() }))
            .catch(async (e) => {
                if (ctl.canceled || e?.canceled) {
                    // explicit cancel: drop the server-side parts too
                    if (ctl.multipart) {
                        try { await s3.post(`/buckets/${job.bucketId}/objects/abort`, ctl.multipart); } catch (_) { /* ignore */ }
                        writeResume((m) => { delete m[resumeId(job.bucketId, job.key, job.file)]; });
                    }
                    patch(job.uid, { status: 'canceled' });
                } else {
                    // failure: keep the uploaded parts; "Retry" (or adding the file again) resumes
                    patch(job.uid, { status: 'error', error: e?.error || e?.message || String(e) });
                }
            })
            .finally(() => {
                delete handles[job.uid];
                pump();
            });
    }
};

/** items: [{file, key}] → queued in this bucket */
export const enqueueUploads = (bucket, items) => {
    const list = (items || []).filter((x) => x?.file && x?.key);
    if (!list.length) return;
    jobs = [
        ...jobs,
        ...list.map(({ file, key }) => ({
            uid: `u${Date.now()}-${++seq}`,
            bucketId: bucket.id,
            bucketName: bucket.name,
            file,
            key,
            name: key,
            size: file.size,
            loaded: 0,
            status: 'queued',
        })),
    ];
    emit();
    pump();
};

export const cancelUpload = (uid) => {
    const j = jobs.find((x) => x.uid === uid);
    if (!j) return;
    if (j.status === 'queued') patch(uid, { status: 'canceled' });
    else handles[uid]?.cancel();
};

export const retryUpload = (uid) => {
    const j = jobs.find((x) => x.uid === uid);
    if (j && j.status === 'error') {
        patch(uid, { status: 'queued', error: null });
        pump();
    }
};

export const clearFinished = () => {
    jobs = jobs.filter(isActive);
    emit();
};

/** Drag & drop: DataTransfer → [{file, path}] (folders walked) */
export const filesFromDrop = async (dt) => {
    const items = Array.from(dt?.items || []);
    const entries = items.map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null)).filter(Boolean);
    if (!entries.length) return Array.from(dt?.files || []).map((f) => ({ file: f, path: f.name }));

    const out = [];
    const walk = async (entry, base) => {
        if (entry.isFile) {
            const file = await new Promise((res, rej) => entry.file(res, rej));
            out.push({ file, path: base + file.name });
        } else if (entry.isDirectory) {
            const reader = entry.createReader();
            const all = [];
            // readEntries returns at most 100 entries per call; read until empty
            for (;;) {
                // eslint-disable-next-line no-await-in-loop
                const batch = await new Promise((res, rej) => reader.readEntries(res, rej));
                if (!batch.length) break;
                all.push(...batch);
            }
            for (const c of all) {
                // eslint-disable-next-line no-await-in-loop
                await walk(c, `${base}${entry.name}/`);
            }
        }
    };
    for (const e of entries) {
        // eslint-disable-next-line no-await-in-loop
        await walk(e, '');
    }
    return out;
};
